import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, downloadFile, fmtCount, fmtDuration, fmtUsd, parseSpec } from "../api";
import { useUI } from "../components/UI";
import LeadCard from "../components/LeadCard";
import { AdvancedFields, StopFields } from "../components/SettingsForm";

const TERMINAL = ["done", "error", "cancelled", "interrupted"];

const prettyJSON = (s) => {
  try {
    return JSON.stringify(JSON.parse(s), null, 2);
  } catch {
    return s;
  }
};

const range = (lo, hi) => {
  if (lo != null && hi != null) return `${fmtCount(lo)} to ${fmtCount(hi)}`;
  if (lo != null) return `≥ ${fmtCount(lo)}`;
  if (hi != null) return `≤ ${fmtCount(hi)}`;
  return null;
};

function ArtifactItem({ runId, art }) {
  const [full, setFull] = useState(null);
  const onToggle = async (e) => {
    if (e.target.open && !full) {
      try {
        const { data } = await api.get(`/runs/${runId}/artifacts/${art.id}`);
        setFull(data);
      } catch {
        /* ignore */
      }
    }
  };
  return (
    <details className="artifact" onToggle={onToggle}>
      <summary>
        <span className={`art-step ${art.ok ? "" : "art-bad"}`}>{art.step}</span>
        {art.account && <span className="muted art-acct">{art.account}</span>}
        {art.cost_usd > 0 && <span className="badge">{fmtUsd(art.cost_usd)}</span>}
        {!art.ok && <span className="badge art-bad">error</span>}
        <span className="muted art-time">{new Date(art.created_at + "Z").toLocaleTimeString()}</span>
      </summary>
      {full ? (
        <div className="art-body">
          <div className="art-col">
            <div className="muted">request</div>
            <pre>{prettyJSON(full.request)}</pre>
          </div>
          <div className="art-col">
            <div className="muted">response</div>
            <pre>{prettyJSON(full.response)}</pre>
          </div>
        </div>
      ) : (
        <div className="muted" style={{ padding: 8 }}>loading…</div>
      )}
    </details>
  );
}

function SpecView({ spec, advanced }) {
  if (!spec) return null;
  const followers = range(spec.min_followers, spec.max_followers);
  const posts = range(spec.min_posts, spec.max_posts);
  return (
    <div className="spec">
      <div className="spec-row">
        <span className="spec-k">niche</span>
        <span>{spec.niche}</span>
      </div>
      <div className="spec-row">
        <span className="spec-k">location</span>
        <span>{spec.location || "anywhere"}</span>
      </div>
      {(followers || posts || spec.verified_only) && (
        <div className="spec-row">
          <span className="spec-k">filters</span>
          <span className="row" style={{ gap: 8 }}>
            <span className="badge">public only</span>
            {followers && <span className="badge">followers {followers}</span>}
            {posts && <span className="badge">posts {posts}</span>}
            {spec.verified_only && <span className="badge">verified</span>}
          </span>
        </div>
      )}
      {advanced && (
        <div className="spec-row">
          <span className="spec-k">round 1</span>
          <span className="row" style={{ gap: 6 }}>
            {spec.search_terms.map((t) => (
              <span key={t} className="chip static">"{t}"</span>
            ))}
            {spec.hashtags.map((t) => (
              <span key={t} className="chip static">#{t}</span>
            ))}
          </span>
        </div>
      )}
    </div>
  );
}

const ADD_FIELDS = [
  { k: "add_budget_usd", label: "Add budget", unit: "$", min: 0 },
  { k: "add_rounds", label: "Add rounds", min: 0 },
  { k: "add_target", label: "Add target leads", min: 0 },
];

const SIMPLE_ADD_FIELDS = [
  { k: "add_target", label: "More accounts to find", min: 0 },
  { k: "add_budget_usd", label: "Extra budget", unit: "$", min: 0 },
];

function ContinuePanel({ run, onDone }) {
  const ui = useUI();
  const cfg = parseSpec(run.settings) || {};
  const hitTarget = cfg.target_leads && run.matched >= cfg.target_leads;
  const [add, setAdd] = useState({
    add_budget_usd: 2,
    add_rounds: cfg.max_rounds ? 3 : 0,
    add_target: hitTarget ? cfg.target_leads : 0,
  });
  const [knobs, setKnobs] = useState(cfg);
  const go = () =>
    ui.run(async () => {
      // Simple view: no round limit, so only the target and the budget stop it.
      const body = ui.advanced
        ? { ...add, settings: knobs }
        : { add_budget_usd: add.add_budget_usd, add_target: add.add_target, add_rounds: 0, settings: { max_rounds: 0 } };
      await api.post(`/runs/${run.id}/continue`, body);
      onDone();
    }, "Run continued");
  if (!ui.advanced) {
    return (
      <div className="continue">
        <div className="row">
          <h4>Find more</h4>
          <span className="muted">Picks up where it stopped. Nothing already paid for is bought again.</span>
        </div>
        <StopFields value={add} fields={SIMPLE_ADD_FIELDS} onChange={(k, v) => setAdd((a) => ({ ...a, [k]: v }))} />
        <div className="row">
          <div className="spacer" />
          <button className="btn primary" onClick={go}>Keep going</button>
        </div>
      </div>
    );
  }
  return (
    <div className="continue">
      <div className="row">
        <h4>Keep going</h4>
        <span className="muted">
          Resumes from this run's pool: nothing already paid for is bought again.
        </span>
      </div>
      <StopFields value={add} fields={ADD_FIELDS} onChange={(k, v) => setAdd((a) => ({ ...a, [k]: v }))} />
      <details className="sf-adv">
        <summary>Change any stage for the rest of the run</summary>
        <AdvancedFields value={knobs} onChange={(k, v) => setKnobs((s) => ({ ...s, [k]: v }))} />
      </details>
      <div className="row">
        <div className="spacer" />
        <button className="btn primary" onClick={go}>Continue run</button>
      </div>
    </div>
  );
}

function SourcesTable({ sources }) {
  if (!sources.length) return <div className="muted">nothing searched yet</div>;
  return (
    <div className="table-scroll">
      <table className="data">
        <thead>
          <tr>
            <th>rnd</th>
            <th>source</th>
            <th>why</th>
            <th className="num">depth</th>
            <th className="num">got</th>
            <th className="num">new</th>
            <th className="num">in range</th>
            <th className="num">matches</th>
            <th className="num">cost</th>
            <th>status</th>
          </tr>
        </thead>
        <tbody>
          {sources.map((s) => (
            <tr key={s.id} className={s.matched ? "hit" : ""}>
              <td className="num">{s.round}</td>
              <td className="mono">{s.kind === "hashtag" ? `#${s.term}` : `"${s.term}"`}</td>
              <td className="muted small">{s.origin}</td>
              <td className="num">{s.depth}</td>
              <td className="num">{s.items}</td>
              <td className="num">{s.new_accounts}</td>
              <td className="num">{s.in_range}</td>
              <td className="num"><strong>{s.matched}</strong></td>
              <td className="num">{fmtUsd(s.cost_usd)}</td>
              <td className={`status-${s.status === "done" ? "done" : s.status === "failed" ? "error" : "queued"}`}>
                {s.status}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const POOL_STATES = ["judged", "candidate", "filtered", "ready", "queued", "unavailable"];

function AccountPool({ runId, open }) {
  const [rows, setRows] = useState(null);
  const [state, setState] = useState("");
  useEffect(() => {
    if (!open) return;
    api.get(`/runs/${runId}/accounts`).then(({ data }) => setRows(data)).catch(() => setRows([]));
  }, [runId, open]);
  if (!rows) return <div className="muted">loading…</div>;
  const counts = rows.reduce((m, r) => ({ ...m, [r.state]: (m[r.state] || 0) + 1 }), {});
  const shown = rows.filter((r) => !state || r.state === state);
  return (
    <>
      <div className="row" style={{ margin: "8px 0", gap: 6 }}>
        <button className={`chip ${!state ? "on" : ""}`} onClick={() => setState("")}>all {rows.length}</button>
        {POOL_STATES.filter((s) => counts[s]).map((s) => (
          <button key={s} className={`chip ${state === s ? "on" : ""}`} onClick={() => setState(s)}>
            {s} {counts[s]}
          </button>
        ))}
      </div>
      <div className="table-scroll pool">
        <table className="data">
          <thead>
            <tr>
              <th>account</th>
              <th>state</th>
              <th className="num">followers</th>
              <th className="num">score</th>
              <th>why</th>
              <th>found via</th>
              <th className="num">rnd</th>
            </tr>
          </thead>
          <tbody>
            {shown.slice(0, 1000).map((r) => (
              <tr key={r.username} className={r.match ? "hit" : ""}>
                <td className="mono">
                  <a href={`https://instagram.com/${r.username}`} target="_blank" rel="noreferrer">@{r.username}</a>
                </td>
                <td>{r.match ? "match" : r.state}</td>
                <td className="num">{fmtCount(r.followers)}</td>
                <td className="num">{r.score ?? ""}</td>
                <td className="muted small">{r.filter_reason}</td>
                <td className="muted small">{r.found_via}</td>
                <td className="num">{r.round}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {shown.length > 1000 && <div className="muted small">showing the first 1000 of {shown.length}</div>}
      </div>
    </>
  );
}

export default function RunDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const ui = useUI();
  const { advanced } = ui;
  const [run, setRun] = useState(null);
  const [logs, setLogs] = useState([]);
  const [leads, setLeads] = useState([]);
  const [artifacts, setArtifacts] = useState([]);
  const [sources, setSources] = useState([]);
  const [poolOpen, setPoolOpen] = useState(false);
  const [stepFilter, setStepFilter] = useState("");
  const [autoscroll, setAutoscroll] = useState(true);
  const lastId = useRef(0);
  const busy = useRef(false);
  const logBox = useRef(null);
  const status = useRef(null);

  const tick = useCallback(async () => {
    if (busy.current) return; // avoid overlapping polls double-appending
    busy.current = true;
    try {
      const [{ data: r }, { data: newLogs }, { data: ls }, { data: arts }, { data: srcs }] = await Promise.all([
        api.get(`/runs/${id}`),
        api.get(`/runs/${id}/logs`, { params: { after_id: lastId.current } }),
        api.get(`/runs/${id}/leads`),
        api.get(`/runs/${id}/artifacts`),
        api.get(`/runs/${id}/sources`),
      ]);
      setRun(r);
      status.current = r.status;
      setLeads(ls);
      setArtifacts(arts);
      setSources(srcs);
      if (newLogs.length) {
        lastId.current = Math.max(lastId.current, ...newLogs.map((l) => l.id));
        setLogs((prev) => {
          const seen = new Set(prev.map((l) => l.id));
          return [...prev, ...newLogs.filter((l) => !seen.has(l.id))];
        });
      }
    } catch (e) {
      /* keep polling */
    } finally {
      busy.current = false;
    }
  }, [id]);

  useEffect(() => {
    setLogs([]);
    setLeads([]);
    lastId.current = 0;
    status.current = null;
    tick();
    const t = setInterval(() => {
      if (TERMINAL.includes(status.current)) return;
      tick();
    }, 1500);
    return () => clearInterval(t);
  }, [id, tick]);

  // one final drain after the run goes terminal
  useEffect(() => {
    if (run && TERMINAL.includes(run.status)) tick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run?.status]);

  useEffect(() => {
    if (autoscroll && logBox.current) logBox.current.scrollTop = logBox.current.scrollHeight;
  }, [logs, autoscroll]);

  const cancel = () =>
    ui.run(async () => {
      await api.post(`/runs/${id}/cancel`);
      tick();
    }, "Cancel requested");

  const del = async () => {
    if (!window.confirm(`Delete search #${id} and its results?`)) return;
    const ok = await ui.run(async () => {
      await api.delete(`/runs/${id}`);
      return true;
    }, "Search deleted");
    if (ok) navigate("/");
  };

  const exportCsv = () => ui.run(() => downloadFile(`/runs/${id}/leads.csv`, `leads-search-${id}.csv`));

  const live = run && !TERMINAL.includes(run.status);
  const spec = parseSpec(run?.spec);
  const cfg = parseSpec(run?.settings) || {};
  const budget = cfg.budget_usd || 0;
  const spentPct = budget ? Math.min(100, ((run?.spent_usd || 0) / budget) * 100) : 0;

  return (
    <div>
      <div className="row" style={{ marginBottom: 12 }}>
        <Link to="/" className="muted">← new search</Link>
        <div className="spacer" />
        {live && <button className="btn" onClick={cancel}>Cancel</button>}
        {leads.length > 0 && (
          <button className="btn primary" onClick={exportCsv}>Export CSV</button>
        )}
        {!live && <button className="btn danger" onClick={del}>Delete</button>}
      </div>

      {run && (
        <>
          <h2 className="run-query">{run.query}</h2>
          <div className="row" style={{ gap: 14, margin: "10px 0 16px" }}>
            <span className={`status-${run.status}`} style={{ fontWeight: 600 }}>
              {live && <span className="pulse" />}
              {run.status}
            </span>
            <span className="badge">
              found {run.matched}{cfg.target_leads ? ` of ${cfg.target_leads}` : ""}
            </span>
            {advanced && <span className="badge">sourced {run.sourced}</span>}
            {advanced && <span className="badge">checked {run.evaluated}</span>}
            {advanced && (
              <span className="badge">round {run.round}{cfg.max_rounds ? ` of ${cfg.max_rounds}` : ""}</span>
            )}
            <span className="badge">{fmtDuration(run.active_seconds)}</span>
            {run.status === "error" && run.detail && <span className="status-error">{run.detail}</span>}
          </div>
          {budget > 0 && (
            <div className="budget">
              <div className="budget-bar"><div style={{ width: `${spentPct}%` }} /></div>
              <span className="mono">{fmtUsd(run.spent_usd)} of {fmtUsd(budget)} spent</span>
            </div>
          )}
          {run.stop_reason && !live && (
            <div className="stop-reason">
              <span className="spec-k">stopped</span> {run.stop_reason}
            </div>
          )}
          {live && !advanced && logs.length > 0 && (
            <div className="live-line">{logs[logs.length - 1].message}</div>
          )}
          <SpecView spec={spec} advanced={advanced} />
          {!live && ["done", "cancelled", "interrupted", "error"].includes(run.status) && (
            <ContinuePanel key={`${run.id}-${run.round}-${run.status}`} run={run} onDone={tick} />
          )}
        </>
      )}

      <div className="row" style={{ margin: "26px 0 12px" }}>
        <h3>Matches</h3>
        <span className="muted">{leads.length} account{leads.length === 1 ? "" : "s"}, best first</span>
      </div>
      {leads.length === 0 ? (
        <p className="muted">
          {live ? "Searching… matches appear here as they are confirmed." : "No matching accounts."}
        </p>
      ) : (
        <div className="grid">
          {leads.map((l) => (
            <LeadCard key={l.id} lead={l} />
          ))}
        </div>
      )}

      {advanced && (
        <>
          <details className="section" open>
            <summary>
              <h4>Sources</h4>
              <span className="muted">
                {sources.length} keyword/hashtag quer{sources.length === 1 ? "y" : "ies"}, what each yielded and cost
              </span>
            </summary>
            <SourcesTable sources={sources} />
          </details>

          <details className="section" onToggle={(e) => setPoolOpen(e.target.open)}>
            <summary>
              <h4>Account pool</h4>
              <span className="muted">every account found and how far it got</span>
            </summary>
            <AccountPool runId={id} open={poolOpen} />
          </details>

          <details className="section" open={live || undefined}>
            <summary>
              <h4>Activity log</h4>
              <span className="muted">{logs.length} lines{live ? " · live" : ""}</span>
            </summary>
            <div className="row" style={{ margin: "8px 0" }}>
              <div className="spacer" />
              <label className="muted" style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <input type="checkbox" style={{ width: "auto" }} checked={autoscroll} onChange={(e) => setAutoscroll(e.target.checked)} />
                autoscroll
              </label>
            </div>
            <div className="logpanel" ref={logBox}>
              {logs.length === 0 ? (
                <div className="log-line log-info">waiting for logs…</div>
              ) : (
                logs.map((l) => (
                  <div key={l.id} className={`log-line log-${l.level}`}>
                    <span className="log-ts">{new Date(l.created_at + "Z").toLocaleTimeString()}</span>
                    <span className="log-msg">{l.message}</span>
                  </div>
                ))
              )}
            </div>
          </details>

          <details className="section">
            <summary>
              <h4>I/O snapshots</h4>
              <span className="muted">{artifacts.length} external call(s)</span>
            </summary>
            <div className="row" style={{ margin: "8px 0" }}>
              <span className="muted">Exact request and response of every Apify and Claude call. Click to expand.</span>
              <div className="spacer" />
              <select value={stepFilter} onChange={(e) => setStepFilter(e.target.value)}>
                <option value="">all steps</option>
                {[...new Set(artifacts.map((a) => a.step))].map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="artifacts">
              {artifacts
                .filter((a) => !stepFilter || a.step === stepFilter)
                .map((a) => (
                  <ArtifactItem key={a.id} runId={id} art={a} />
                ))}
              {artifacts.length === 0 && <div className="muted">no external calls recorded yet</div>}
            </div>
          </details>
        </>
      )}
    </div>
  );
}
